import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";

import { createDraftLessonBookingInputAtom } from "@/features/BookingWizard/atoms/selectors/createDraftLessonInputAtom/createDraftLessonInputAtom";
import { createLessonRequestBookingInputAtom } from "@/features/BookingWizard/atoms/selectors/createLessonRequestBookingInputAtom/createLessonRequestBookingInputAtom";
import { createRequestBookingInputAtom } from "@/features/BookingWizard/atoms/selectors/createRequestBookingInputAtom/createRequestBookingInputAtom";
import { useCreateDraftLessonBooking } from "@/features/BookingWizard/hooks/useCreateDraftLessonBooking/useCreateDraftLessonBooking";
import { useCreateLessonRequestBooking } from "@/features/BookingWizard/hooks/useCreateLessonRequestBooking/useCreateLessonRequestBooking";
import { useCreateNewRequestBooking } from "@/features/BookingWizard/hooks/useCreateNewRequestBooking/useCreateNewRequestBooking";

import { ClubStackNavigatorParamList } from "../../../../../../navigators/ClubNavigator";
import { createMultiSlotBookingsInputAtom } from "../../../../atoms/selectors/createMultiSlotBookingsInputAtom/createMultiSlotBookingsInputAtom";
import { createSingleSlotBookingsInputAtom } from "../../../../atoms/selectors/createSingleSlotBookingsInputAtom/createSingleSlotBookingsInputAtom";
import {
  BookingWizardDataAtom,
  isCreatingBookingAtom,
} from "../../../../atoms/stores";
import { useCreateNewGolfBooking } from "../../../../hooks/useCreateNewGolfBooking/useCreateNewGolfBooking";
import { useCreateNewMultiSlotBooking } from "../../../../hooks/useCreateNewMultiSlotBooking/useCreateNewMultiSlotBooking";

export const useCompleteBooking = (onClose: () => void) => {
  const bookingWizardData = useAtomValue(BookingWizardDataAtom);

  const isDurationBooking = bookingWizardData?.type === "DurationBooking";
  const isLesson = bookingWizardData?.type === "LessonBooking";
  const isRequest = bookingWizardData?.type === "RequestBooking";
  const isLessonRequest =
    isLesson &&
    bookingWizardData?.maxTime &&
    bookingWizardData?.minTime &&
    bookingWizardData?.requestedTime;

  const createDraftLessonInput = useAtomValue(
    createDraftLessonBookingInputAtom
  );
  const [handleCreateDraftLessonBooking, isCreatingDraftLesson] =
    useCreateDraftLessonBooking();

  const [handleCreateLessonRequestBooking, isCreatingLessonRequest] =
    useCreateLessonRequestBooking();

  const createLessonRequestInput = useAtomValue(
    createLessonRequestBookingInputAtom
  );
  const createSingleSlotInput = useAtomValue(createSingleSlotBookingsInputAtom);
  const [handleCreateBooking, isCreating] = useCreateNewGolfBooking();

  const createMultiSlotInput = useAtomValue(createMultiSlotBookingsInputAtom);
  const [handleCreateMultiSlotBooking, isCreatingMultiSlot] =
    useCreateNewMultiSlotBooking();

  const createRequestBookingInput = useAtomValue(createRequestBookingInputAtom);
  const [handleCreateRequestBooking, isCreatingRequest] =
    useCreateNewRequestBooking();

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const handleCompleteBooking = useCallback(() => {
    const navigateToSummary = (
      bookingId: string,
      params: ClubStackNavigatorParamList["BookingSummary"]["params"] = {}
    ) => {
      onClose();
      navigation.navigate("BookingSummary", {
        slotOrBookingId: bookingId,
        params,
      });
    };

    if (isLessonRequest && createLessonRequestInput) {
      handleCreateLessonRequestBooking(createLessonRequestInput, (bookingId) =>
        navigateToSummary(bookingId)
      );
      return;
    } else if (isLesson && createDraftLessonInput) {
      handleCreateDraftLessonBooking(createDraftLessonInput, (bookingId) =>
        navigateToSummary(bookingId)
      );
      return;
    } else if (isDurationBooking && createMultiSlotInput) {
      handleCreateMultiSlotBooking(createMultiSlotInput, (bookingId) =>
        navigateToSummary(bookingId)
      );
      return;
    } else if (isRequest && createRequestBookingInput) {
      const recurrence =
        bookingWizardData.type === "RequestBooking"
          ? bookingWizardData.recurrence
          : undefined;

      const pendingRecurrence = recurrence?.enabled
        ? recurrence.endCondition === "ON_DATE"
          ? {
              recurrenceType: recurrence.recurrenceType,
              repeatInterval: recurrence.repeatInterval,
              endCondition: "ON_DATE" as const,
              endDate: recurrence.endDate,
            }
          : {
              recurrenceType: recurrence.recurrenceType,
              repeatInterval: recurrence.repeatInterval,
              endCondition: "AFTER_OCCURRENCES" as const,
              occurrenceCount: recurrence.occurrenceCount ?? 4,
            }
        : undefined;

      handleCreateRequestBooking(createRequestBookingInput, (bookingId) => {
        navigateToSummary(bookingId, { pendingRecurrence });
      });
      return;
    } else if (createSingleSlotInput) {
      handleCreateBooking(createSingleSlotInput, (bookingId) => {
        navigateToSummary(bookingId);
      });
    }
  }, [
    isLesson,
    createDraftLessonInput,
    handleCreateDraftLessonBooking,
    isDurationBooking,
    createMultiSlotInput,
    handleCreateMultiSlotBooking,
    isRequest,
    bookingWizardData,
    createRequestBookingInput,
    handleCreateRequestBooking,
    createSingleSlotInput,
    handleCreateBooking,
    onClose,
    navigation,
    createLessonRequestInput,
    isLessonRequest,
    handleCreateLessonRequestBooking,
  ]);

  const isLoading =
    isCreating ||
    isCreatingMultiSlot ||
    isCreatingDraftLesson ||
    isCreatingRequest ||
    isCreatingLessonRequest;

  const setIsCreatingBooking = useSetAtom(isCreatingBookingAtom);

  // Sync isCreatingBookingAtom with isLoading state
  useEffect(() => {
    setIsCreatingBooking(isLoading);
  }, [isLoading, setIsCreatingBooking]);

  return [handleCompleteBooking, isLoading] as const;
};
